import { useState } from "react";
import { ThumbsUp, ThumbsDown, X } from "lucide-react";
import { motion, AnimatePresence } from "framer-motion";
import { supabase } from "@/integrations/supabase/client";
import { useAuth } from "@/hooks/useAuth";
import { useLocation } from "react-router-dom";

type Step = "idle" | "open" | "comment" | "done";

export const FeedbackWidget = () => {
  const { user } = useAuth();
  const location = useLocation();
  const [step, setStep] = useState<Step>("idle");
  const [rating, setRating] = useState<"up" | "down" | null>(null);
  const [comment, setComment] = useState("");
  const [submitting, setSubmitting] = useState(false);

  // Don't show on auth page
  if (!user || location.pathname === "/auth") return null;

  const handleRate = (value: "up" | "down") => {
    setRating(value);
    setStep("comment");
  };

  const handleSubmit = async () => {
    if (!rating || submitting) return;
    setSubmitting(true);
    try {
      await supabase.functions.invoke("send-feedback", {
        body: {
          user_id: user.id,
          user_email: user.email,
          rating,
          comment: comment.trim() || null,
          page: location.pathname,
        },
      });
    } catch {
      // Fail silently — feedback is non-critical
    } finally {
      setSubmitting(false);
      setStep("done");
      // Auto-collapse after 3 seconds
      setTimeout(() => setStep("idle"), 3000);
    }
  };

  const handleClose = () => {
    setStep("idle");
    setRating(null);
    setComment("");
  };

  return (
    <div className="fixed bottom-6 right-6 z-50 flex flex-col items-end gap-2">
      <AnimatePresence mode="wait">
        {/* Expanded panel */}
        {(step === "open" || step === "comment" || step === "done") && (
          <motion.div
            key="panel"
            initial={{ opacity: 0, scale: 0.92, y: 8 }}
            animate={{ opacity: 1, scale: 1, y: 0 }}
            exit={{ opacity: 0, scale: 0.92, y: 8 }}
            transition={{ duration: 0.18 }}
            className="bg-card border border-border rounded-2xl shadow-xl p-4 w-72"
          >
            {/* Header */}
            <div className="flex items-center justify-between mb-3">
              <span className="text-sm font-semibold text-foreground">
                {step === "done" ? "Thank you! 🙏" : "How are we doing?"}
              </span>
              <button
                onClick={handleClose}
                className="text-muted-foreground hover:text-foreground transition-colors"
              >
                <X className="w-4 h-4" />
              </button>
            </div>

            {/* Step: choose rating */}
            {step === "open" && (
              <>
                <p className="text-xs text-muted-foreground mb-4">
                  Is Resume Flow AI helping you land more interviews?
                </p>
                <div className="flex gap-3">
                  <button
                    onClick={() => handleRate("up")}
                    className="flex-1 flex flex-col items-center gap-1.5 py-3 rounded-xl border-2 border-border hover:border-green-500 hover:bg-green-500/10 transition-all group"
                  >
                    <ThumbsUp className="w-6 h-6 text-muted-foreground group-hover:text-green-600 transition-colors" />
                    <span className="text-xs text-muted-foreground group-hover:text-green-600">Yes!</span>
                  </button>
                  <button
                    onClick={() => handleRate("down")}
                    className="flex-1 flex flex-col items-center gap-1.5 py-3 rounded-xl border-2 border-border hover:border-red-400 hover:bg-red-500/10 transition-all group"
                  >
                    <ThumbsDown className="w-6 h-6 text-muted-foreground group-hover:text-red-500 transition-colors" />
                    <span className="text-xs text-muted-foreground group-hover:text-red-500">Needs work</span>
                  </button>
                </div>
              </>
            )}

            {/* Step: optional comment */}
            {step === "comment" && (
              <>
                <div className="flex items-center gap-2 mb-3">
                  {rating === "up" ? (
                    <ThumbsUp className="w-4 h-4 text-green-600" />
                  ) : (
                    <ThumbsDown className="w-4 h-4 text-red-500" />
                  )}
                  <span className="text-xs text-muted-foreground">
                    {rating === "up" ? "Glad to hear it!" : "Sorry to hear that."}
                    {" "}Anything to add? <span className="italic">(optional)</span>
                  </span>
                </div>
                <textarea
                  value={comment}
                  onChange={(e) => setComment(e.target.value)}
                  placeholder="What do you love? What's missing?"
                  rows={3}
                  maxLength={500}
                  className="w-full text-sm bg-muted/50 border border-border rounded-lg px-3 py-2 resize-none focus:outline-none focus:ring-2 focus:ring-primary/40 text-foreground placeholder:text-muted-foreground"
                />
                <div className="flex gap-2 mt-3">
                  <button
                    onClick={handleSubmit}
                    disabled={submitting}
                    className="flex-1 bg-primary text-primary-foreground text-sm font-medium py-2 rounded-lg hover:bg-primary/90 transition-colors disabled:opacity-60"
                  >
                    {submitting ? "Sending..." : "Send Feedback"}
                  </button>
                  <button
                    onClick={handleSubmit}
                    disabled={submitting}
                    className="px-3 py-2 text-xs text-muted-foreground hover:text-foreground border border-border rounded-lg transition-colors"
                  >
                    Skip
                  </button>
                </div>
              </>
            )}

            {/* Step: done */}
            {step === "done" && (
              <p className="text-sm text-muted-foreground">
                Your feedback helps us improve Resume Flow AI for everyone. We really appreciate it!
              </p>
            )}
          </motion.div>
        )}
      </AnimatePresence>

      {/* Trigger button — always visible */}
      <motion.button
        onClick={() => step === "idle" ? setStep("open") : handleClose()}
        whileHover={{ scale: 1.05 }}
        whileTap={{ scale: 0.96 }}
        className="flex items-center gap-2 bg-card border border-border text-foreground text-sm font-medium px-4 py-2.5 rounded-full shadow-lg hover:shadow-xl hover:border-primary/40 transition-all"
        title="Like the app?"
      >
        <ThumbsUp className="w-4 h-4 text-primary" />
        <span>Like the app?</span>
      </motion.button>
    </div>
  );
};
